import { useId } from "react";
import { formatDate } from "../format.js";
import { getFamilyPracticeTodos, type PracticeTodo } from "../practiceTodos.js";
import { getFamilyStudentExamProjects } from "../examPreparation.js";
import type { FamilyStudentExamProject } from "../examPreparation.js";
import { useAsync } from "../useAsync.js";
import "./ExamProjects.css";

function dateLabel(project: FamilyStudentExamProject): string {
  if (!project.examDate) return "Date not set";
  return formatDate(project.examDate + "T12:00:00Z")
    + (project.examDateKind === "confirmed" ? " · confirmed" : " · tentative");
}

function titleLabel(project: FamilyStudentExamProject): string {
  return [
    project.board,
    project.examType,
    project.grade ? "Grade " + project.grade : null,
    project.instrument,
  ].filter(Boolean).join(" · ") || "Exam preparation";
}

function practiceTasksForProject(
  project: FamilyStudentExamProject,
  practiceTodos: PracticeTodo[],
): PracticeTodo[] {
  if (project.status !== "active") return [];
  const repertoireIds = new Set(project.linkedRepertoire.map((piece) => piece.id));
  if (repertoireIds.size === 0) return [];
  return practiceTodos.filter((todo) =>
    !todo.completed
    && !todo.withdrawnAt
    && todo.repertoireId !== null
    && repertoireIds.has(todo.repertoireId),
  );
}

function ExamPlanCard({
  project,
  practiceTodos,
  practiceLoading,
  practiceError,
}: {
  project: FamilyStudentExamProject;
  practiceTodos: PracticeTodo[];
  practiceLoading: boolean;
  practiceError: boolean;
}) {
  const titleId = useId();
  const title = titleLabel(project);
  const currentTasks = practiceTasksForProject(project, practiceTodos);

  return (
    <article className="panel stack exam-projects-card">
      <div>
        <h3 id={titleId} className="exam-projects-title">{title}</h3>
        <p className="small muted exam-projects-meta">
          {dateLabel(project)}
          {project.status === "completed" ? " · Completed" : project.status === "withdrawn" ? " · Withdrawn" : ""}
        </p>
      </div>
      {project.entryMilestone && (
        <p className="exam-projects-copy"><strong>Next milestone:</strong> {project.entryMilestone}</p>
      )}
      {project.requirements && <p className="exam-projects-copy">{project.requirements}</p>}
      {project.linkedGoals.length > 0 && (
        <div className="small">
          <strong>Goals and milestones</strong>
          <ul>
            {project.linkedGoals.map((goal) => (
              <li key={goal.id}>
                {goal.title}
                {goal.targetDate && <span className="muted"> · target {formatDate(goal.targetDate + "T12:00:00Z")}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
      {project.linkedRepertoire.length > 0 && (
        <div className="small">
          <strong>Repertoire</strong>
          <ul>
            {project.linkedRepertoire.map((piece) => (
              <li key={piece.id}>{piece.composer ? piece.composer + " — " : ""}{piece.title}</li>
            ))}
          </ul>
        </div>
      )}
      {project.status === "active" && project.linkedRepertoire.length > 0 && (
        <div className="exam-projects-current-tasks" aria-labelledby={titleId + "-tasks"}>
          <h4 id={titleId + "-tasks"}>Current practice tasks</h4>
          {practiceError ? null : practiceLoading && practiceTodos.length === 0 ? (
            <p className="small muted" role="status">Loading current practice tasks…</p>
          ) : currentTasks.length > 0 ? (
            <ul className="exam-projects-task-list">
              {currentTasks.map((task) => (
                <li key={task.id}>
                  <strong>{task.title}</strong>
                  {task.suggestedMinutes !== null && (
                    <span className="small muted"> · suggested {task.suggestedMinutes} min</span>
                  )}
                  {task.description && <div className="small muted">{task.description}</div>}
                </li>
              ))}
            </ul>
          ) : (
            <p className="small muted">No current practice tasks are linked to this exam repertoire.</p>
          )}
        </div>
      )}
      {project.result && <p className="small"><strong>Result:</strong> {project.result}</p>}
    </article>
  );
}

function FamilyExamProjectsContent({
  studentId,
  projects,
}: {
  studentId: string;
  projects: FamilyStudentExamProject[];
}) {
  const hasActiveLinkedRepertoire = projects.some(
    (project) => project.status === "active" && project.linkedRepertoire.length > 0,
  );
  const practice = useAsync(
    () => hasActiveLinkedRepertoire ? getFamilyPracticeTodos(studentId) : Promise.resolve([]),
    [studentId, hasActiveLinkedRepertoire],
  );
  const practiceTodos = practice.data ?? [];

  return (
    <>
      {hasActiveLinkedRepertoire && practice.error && (
        <div className="notice small" role="alert">
          Practice tasks could not be loaded just now. Exam plans are still available.
          <button type="button" className="quiet small" onClick={practice.reload}>Retry practice tasks</button>
        </div>
      )}
      {projects.map((project) => (
        <ExamPlanCard
          key={project.id}
          project={project}
          practiceTodos={practiceTodos}
          practiceLoading={practice.loading}
          practiceError={Boolean(practice.error)}
        />
      ))}
    </>
  );
}

export function FamilyExamProjects({ studentId }: { studentId: string }) {
  const headingId = useId();
  const projects = useAsync(() => getFamilyStudentExamProjects(studentId), [studentId]);

  if (projects.error === "Not found.") return null;
  if (projects.error) {
    return (
      <section className="notice" aria-labelledby={headingId} role="alert">
        <h2 id={headingId}>Exam preparation</h2>
        <p className="small">Exam preparation could not be loaded just now.</p>
        <button type="button" className="quiet small" onClick={projects.reload}>Retry exam preparation</button>
      </section>
    );
  }
  if (!projects.data || projects.data.length === 0) return null;

  return (
    <section className="stack" aria-labelledby={headingId}>
      <div>
        <h2 id={headingId}>Exam preparation</h2>
        <p className="small muted">Tutor-shared dates, requirements and learning milestones.</p>
      </div>
      <FamilyExamProjectsContent key={studentId} studentId={studentId} projects={projects.data} />
    </section>
  );
}
